import React from 'react';
import PropTypes from 'prop-types';

import utils from './utils';
import { toast } from '../toast';

import Avatar from '../Components/Avatar';
import CommentEdit from './CommentEdit';
import CommentField from './CommentField';
import RepliesList from './RepliesList';
import ReplyEdit from './ReplyEdit';

class ExpandedComment extends React.Component {
    state = {
        editing: false,
        replyEditing: false
    };

    focus = () => {
        this.commentField && this.commentField.setFocus();
    }

    setEditing = value =>
        this.setState({ editing: value });

    toggleResolve = () => {
        if (this.commentField.isProcessing()) return Promise.reject();

        this.commentField.setProcessing(true);

        const updatedComment = {
            id: this.props.comment.id,
            resolved: !this.props.comment.resolved
        };

        this.props.commentsStore.api
            .updateComment(this.props.commentsStore.resource, updatedComment)
            .then(updated => {
                this.commentField && this.commentField.setProcessing(false);
                this.props.commentsStore.replaceComment(updated);
            })
            .catch(err => {
                this.commentField && this.commentField.setProcessing(false);
                toast('ERROR_OCCURED');
            });
    };

    deleteComment = () => {
        if (this.commentField.isProcessing() || this.props.comment.replies.length !== 0) {
            return Promise.reject();
        }

        this.props.commentsStore.confirmDelete()
            .then(() => {
                this.commentField.setProcessing(true);

                this.props.commentsStore.api
                    .deleteComment(
                        this.props.commentsStore.resource,
                        this.props.comment.id
                    )
                    .then(res => this.props.commentsStore.removeComment(this.props.comment.id))
                    .catch(err => toast('ERROR_OCCURED'));
            });
    };

    editComment = () =>
        this.setEditing(true);

    exitEditing = () =>
        this.setEditing(false);

    setReplyEditing = value =>
        this.setState({ replyEditing: value });

    reply = () => {
        if (this.commentField.isProcessing() ||
            this.commentField.hasError() ||
            this.commentField.isEmpty) return Promise.reject();

        this.commentField.setProcessing(true);

        const reply = {
            parent: this.props.comment.id,
            content: this.commentField.getContent()
        };

        const mentions = utils.getMentions(reply.content);

        return this.props.commentsStore.checkMentionPermissionsAndAlert(mentions)
            .then(() => {
                this.props.commentsStore.api
                    .submitReply(this.props.commentsStore.resource, reply, mentions)
                    .then(res => {
                        const newComment = this.props.comment.toRaw();
                        newComment.replies.push(res);
                        this.commentField && this.commentField.setProcessing(false);
                        this.commentField.clear();
                        if (this.props.comment.resolved) {
                            const updatedComment = {
                                id: this.props.comment.id,
                                resolved: !this.props.comment.resolved
                            };

                            this.props.commentsStore.api
                                .updateComment(
                                    this.props.commentsStore.resource,
                                    updatedComment
                                )
                                .then(() => {
                                    newComment.resolved = false;
                                    this.props.commentsStore.replaceComment(newComment);
                                });
                        } else {
                            this.props.commentsStore.replaceComment(newComment);
                        }
                    })
                    .catch(err => {
                        console.log(err);
                        toast('ERROR_OCCURED');
                        this.commentField && this.commentField.setProcessing(false);
                    });
            });
    };

    clearComment = () => this.commentField.clear();

    stopPropagation = e => e.stopPropagation();

    renderReplyAuthorActions = () =>
        (<span className='author-actions'>
            <span
                className='icon icon-edit'
                title={ gettext('Edit') }
                onClick={this.editComment}
                ga-action={this.props.commentsStore.GASettings.action}
                ga-label='Edit_Comment'
                data-what='comment-action'
                data-which='edit'
            />
            <span
                className='icon icon-delete'
                title={gettext('Delete')}
                onClick={this.deleteComment}
                ga-action={this.props.commentsStore.GASettings.action}
                ga-label='Delete_Comment'
                data-what='comment-action'
                data-which='delete'
            />
        </span>);

    renderUserActions = () =>
        (<div className='btn-column'>
            <span
                className={`icon icon-status-ok ${this.props.comment.resolved ? 'tool-active' : ''}`}
                title={this.props.comment.resolved ? gettext('Resolved') : gettext('Resolve')}
                onClick={Settings.user.isAuthenticated ? this.toggleResolve : null}
                ga-action={this.props.commentsStore.GASettings.action}
                ga-label='Resolve_Comment'
                data-what='comment-action'
                data-which='resolve'
            />
            {
                this.props.comment.owner.login === Settings.user.login &&
                this.props.comment.replies.length === 0 &&
                utils.checkUpdatePermission(this.props.comment.data.pub_date) &&
                    this.renderReplyAuthorActions()
            }
        </div>);

    renderCommentFieldActions = () =>
        (<span className='comment-actions'>
            <a
                className={`comment-action-btn ${this.commentField.hasError() ? 'disabled' : ''}`}
                onClick={ !this.commentField.hasError() ? this.reply : null }
                ga-action={this.props.commentsStore.GASettings.action}
                ga-label='New_Comment_Reply'
                data-what='comment-action'
                data-which='reply'
            >{ gettext('Reply') }</a>
            <a
                className='comment-action-btn'
                onClick={this.commentField.clear}
                ga-action={this.props.commentsStore.GASettings.action}
                ga-label='Cancel_Comment_Reply'
                data-what='comment-action'
                data-which='cancel-reply'
            >{ gettext('Cancel') }</a>
        </span>);

    render () {
        return (<div
            className='comment-expanded'
            onClick={this.stopPropagation}
        >
            <div className='comment-one-liner'>
                <Avatar
                    name={this.props.comment.author}
                    email={this.props.comment.owner.email}
                    login={this.props.comment.owner.login}
                    classes={'comment-avatar'}
                />
                <div className='comment-column'>
                    <div className='posted-info'>
                        <span className='user' data-what='comment-author'>
                            { this.props.comment.author }
                        </span>
                        <span className='pub-date' data-what='comment-date'>
                            { this.props.comment.published }
                        </span>
                    </div>
                    <div
                        className='content comment-text'
                        dangerouslySetInnerHTML={utils.createMarkup(this.props.comment.content)}
                        data-what='comment-body'
                    />
                </div>
                {
                    this.props.comment.owner.login === Settings.user.login &&
                        this.renderUserActions()
                }
            </div>
            {
                this.props.comment.replies.length > 0 &&
                    <RepliesList
                        comment={this.props.comment}
                        replies={this.props.comment.replies}
                        expandedComponent={this}
                        commentsStore={this.props.commentsStore}
                    />
            }
            {
                this.state.editing &&
                    <CommentEdit
                        comment={this.props.comment}
                        onEscape={this.exitEditing}
                        commentsStore={this.props.commentsStore}
                        expandedComment={this}
                    />
            }
            {
                this.state.replyEditing &&
                    <ReplyEdit
                        reply={this.state.replyEditing}
                        comment={this.props.comment}
                        expandedComponent={this}
                        commentsStore={this.props.commentsStore}
                    />
            }
            {
                !this.state.editing &&
                !this.state.replyEditing &&
                this.props.commentsStore.canComment &&
                    <CommentField
                        ref={commentField => { this.commentField = commentField; }}
                        onChange={this.onCommentInput}
                        onEnter={this.reply}
                        onEscape={this.clearComment}
                        childrenFunc={this.renderCommentFieldActions}
                        placeholder={
                            this.props.commentsStore.resource.exists
                                ? this.props.comment.resolved
                                    ? gettext('Replying will re-open this issue.')
                                    : this.props.commentsStore.canComment
                                        ? gettext('Reply...')
                                        : gettext('Please sign in to post replies.')
                                : gettext('Restore the resource to be able to post replies.')
                        }
                        commentsStore={this.props.commentsStore}
                    />
            }
        </div>);
    }
};

export default ExpandedComment;

ExpandedComment.propTypes = {
    comment: PropTypes.object,
    commentsStore: PropTypes.object.isRequired
};
